import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Users, ListChecks, ShieldCheck, TrendingUp, Clock } from 'lucide-react';
import { api } from '../../api/client';
import Counter from '../../components/Counter';

interface Stats {
  users: { total: number; new7d: number; new30d: number };
  wishlist: { total: number; signupsByDay: Record<string, number> };
  kyc: { verified: number; pending: number; rejected: number };
  moduleAdoption: Record<string, number>;
}

interface RecentSignup {
  id: number;
  email: string;
  fullName: string | null;
  createdAt: string;
}

const MODULE_LABELS: Record<string, string> = {
  hca: 'HCA',
  creditBuilder: 'Credit Builder',
  creditProtect: 'Credit Protect',
  mfaf: 'MFAF',
  scoretrade: 'ScoreTrade',
  virtualCards: 'Virtual Cards',
  fame: 'FAME',
  equityShield: 'Equity Shield',
  capitalPools: 'Capital Pools',
  cryptoWallet: 'Crypto Wallet',
  insurance: 'Insurance',
  gamification: 'Gamification',
};

const KYC_COLORS: Record<string, string> = {
  Verified: '#10B981',
  Pending: '#F59E0B',
  Rejected: '#EF4444',
};

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: number; sub?: string }) {
  return (
    <div className="bg-surface1 rounded-xl p-6 border border-border hover:border-gold/20 transition">
      <div className="flex items-center justify-between mb-3">
        <p className="text-textTertiary text-sm font-semibold uppercase tracking-wide">{label}</p>
        <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">{icon}</div>
      </div>
      <h2 className="text-3xl font-bold text-textPrimary">
        <Counter value={value} />
      </h2>
      {sub && <p className="text-textTertiary text-sm mt-1">{sub}</p>}
    </div>
  );
}

function timeAgo(iso: string) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Overview() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [recent, setRecent] = useState<RecentSignup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/admin/stats'),
      api.get('/admin/wishlist', { params: { per_page: 8 } }),
    ])
      .then(([statsRes, wishlistRes]) => {
        setStats(statsRes.data);
        setRecent(wishlistRes.data.items);
      })
      .catch(() => setError('Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-textSecondary">Loading...</div>;
  if (error || !stats) return <div className="p-8 text-error">{error || 'No data'}</div>;

  const moduleData = Object.entries(stats.moduleAdoption).map(([key, value]) => ({
    name: MODULE_LABELS[key] ?? key,
    count: value,
  }));

  const signupData = Object.entries(stats.wishlist.signupsByDay)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date: date.slice(5), count }));

  const kycData = [
    { name: 'Verified', value: stats.kyc.verified },
    { name: 'Pending', value: stats.kyc.pending },
    { name: 'Rejected', value: stats.kyc.rejected },
  ].filter((d) => d.value > 0);

  return (
    <div className="p-8">
      <h1 className="font-display text-2xl font-bold text-textPrimary mb-6">Overview</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={<Users className="text-gold" size={16} />} label="Total Users" value={stats.users.total} sub={`+${stats.users.new7d} this week`} />
        <StatCard icon={<ListChecks className="text-gold" size={16} />} label="Wishlist Signups" value={stats.wishlist.total} />
        <StatCard icon={<ShieldCheck className="text-gold" size={16} />} label="KYC Verified" value={stats.kyc.verified} sub={`${stats.kyc.pending} pending`} />
        <StatCard icon={<TrendingUp className="text-gold" size={16} />} label="New Users (30d)" value={stats.users.new30d} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-surface1 rounded-xl p-6 border border-border lg:col-span-2">
          <h3 className="text-textPrimary font-semibold mb-4">Module Adoption</h3>
          {moduleData.every((d) => d.count === 0) ? (
            <p className="text-textTertiary text-sm">No module activity yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={moduleData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2E2E2E" />
                <XAxis dataKey="name" tick={{ fill: '#9B9B9B', fontSize: 11 }} angle={-25} textAnchor="end" height={70} />
                <YAxis tick={{ fill: '#9B9B9B', fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #2E2E2E' }} labelStyle={{ color: '#E5E2E1' }} />
                <Bar dataKey="count" fill="#F2CA50" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-surface1 rounded-xl p-6 border border-border">
          <h3 className="text-textPrimary font-semibold mb-4">KYC Status</h3>
          {kycData.length === 0 ? (
            <p className="text-textTertiary text-sm">No KYC submissions yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={kycData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {kycData.map((d) => (
                    <Cell key={d.name} fill={KYC_COLORS[d.name]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #2E2E2E' }} labelStyle={{ color: '#E5E2E1' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex justify-center gap-4 mt-2 text-xs">
            {kycData.map((d) => (
              <span key={d.name} className="flex items-center gap-1.5 text-textSecondary">
                <span className="w-2 h-2 rounded-full" style={{ background: KYC_COLORS[d.name] }} /> {d.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-surface1 rounded-xl p-6 border border-border lg:col-span-2">
          <h3 className="text-textPrimary font-semibold mb-4">Wishlist Signups (30 days)</h3>
          {signupData.length === 0 ? (
            <p className="text-textTertiary text-sm">No signups in the last 30 days.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={signupData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2E2E2E" />
                <XAxis dataKey="date" tick={{ fill: '#9B9B9B', fontSize: 11 }} />
                <YAxis tick={{ fill: '#9B9B9B', fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #2E2E2E' }} labelStyle={{ color: '#E5E2E1' }} />
                <Line type="monotone" dataKey="count" stroke="#F2CA50" strokeWidth={2} dot={{ fill: '#F2CA50' }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-surface1 rounded-xl p-6 border border-border">
          <h3 className="text-textPrimary font-semibold mb-4">Recent Signups</h3>
          {recent.length === 0 ? (
            <p className="text-textTertiary text-sm">No signups yet.</p>
          ) : (
            <div className="space-y-3">
              {recent.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm">
                  <div className="truncate">
                    <p className="text-textPrimary truncate">{r.fullName || r.email}</p>
                    <p className="text-textTertiary text-xs truncate">{r.email}</p>
                  </div>
                  <span className="flex items-center gap-1 text-textTertiary text-xs flex-shrink-0 ml-2">
                    <Clock size={11} /> {timeAgo(r.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
